import React, { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FilePreviewProvider, useFilePreview } from "@/context/FilePreviewContext";
import FilePreviewDrawer from "@/components/FilePreviewDrawer";
import AlertsBell from "@/components/AlertsBell";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Building2,
  Building,
  Truck,
  UserCog,
  Users,
  CalendarClock,
  CalendarDays,
  Radar,
  Repeat2,
  UploadCloud,
  KeyRound,
  BarChart3,
  ShieldAlert,
  ShieldCheck,
  Briefcase,
  Trophy,
  ClipboardCheck,
  FileSpreadsheet,
  SlidersHorizontal,
  LogOut,
  Ambulance,
  UserRound,
  ScrollText,
} from "lucide-react";

const SECURITY_BADGE_POLL_MS = 60000;

// Small self-contained poll for the open-security-alert count shown as a nav badge.
// Agency admin only; silently ignores errors so a blip never breaks the sidebar.
function useSecurityBadge(enabled) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!enabled) {
      setCount(0);
      return undefined;
    }
    let cancelled = false;
    const load = () => {
      api.get("/security/summary")
        .then((r) => { if (!cancelled) setCount(r.data?.open || 0); })
        .catch(() => {});
    };
    load();
    const t = setInterval(load, SECURITY_BADGE_POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, [enabled]);
  return count;
}

const AGENCY_LINKS = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, roles: ["agency_admin", "dispatcher", "client_user"], end: true },
  { to: "/app/companies", label: "Client Companies", icon: Building2, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/vehicles", label: "Vehicles", icon: Truck, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/drivers", label: "Drivers", icon: UserCog, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/customers", label: "Customers", icon: Users, roles: ["agency_admin", "dispatcher", "client_user"] },
  { to: "/app/rides", label: "Rides", icon: CalendarClock, roles: ["agency_admin", "dispatcher", "client_user"] },
  { to: "/app/calendar", label: "Calendar", icon: CalendarDays, roles: ["agency_admin", "dispatcher", "client_user"] },
  { to: "/app/fleet", label: "Live Fleet", icon: Radar, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/recurring", label: "Recurring", icon: Repeat2, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/import", label: "CSV Import", icon: UploadCloud, roles: ["agency_admin", "dispatcher", "client_user"] },
  { to: "/app/api-keys", label: "API Keys", icon: KeyRound, roles: ["agency_admin"] },
  { to: "/app/billing", label: "Billing", icon: BarChart3, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/alerts", label: "Alert History", icon: ShieldAlert, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/scorecard", label: "Scorecard", icon: Trophy, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/compliance", label: "Compliance", icon: ClipboardCheck, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/exports", label: "Export Templates", icon: FileSpreadsheet, roles: ["agency_admin", "dispatcher"] },
  { to: "/app/settings", label: "Alert Rules", icon: SlidersHorizontal, roles: ["agency_admin"] },
  { to: "/app/audit", label: "Audit Log", icon: ScrollText, roles: ["agency_admin"] },
  { to: "/app/security", label: "Security", icon: ShieldCheck, roles: ["agency_admin"] },
  { to: "/app/prospects", label: "Prospects", icon: Briefcase, roles: ["agency_admin"], platformOnly: true },
  { to: "/app/tenants", label: "Tenants", icon: Building, roles: ["agency_admin"], platformOnly: true },
  { to: "/app/account", label: "Account", icon: UserRound, roles: ["agency_admin", "dispatcher", "client_user"] },
];

export default function Layout() {
  return (
    <FilePreviewProvider>
      <LayoutShell />
    </FilePreviewProvider>
  );
}

function LayoutShell() {
  const { user, logout } = useAuth();
  const { isOpen: previewOpen } = useFilePreview();
  const navigate = useNavigate();
  const roleLabel =
    user?.role === "agency_admin"
      ? "Agency Admin"
      : user?.role === "dispatcher"
      ? "Dispatcher"
      : user?.role === "client_user"
      ? "Client Portal"
      : "Driver";

  const visibleLinks = useMemo(
    () => AGENCY_LINKS.filter((l) => l.roles.includes(user?.role) && (!l.platformOnly || user?.is_platform)),
    [user?.role, user?.is_platform]
  );

  const securityOpenCount = useSecurityBadge(user?.role === "agency_admin");

  const subscription = user?.subscription;
  const subLocked = !!subscription?.locked;
  const subTrialWarning =
    !subLocked && subscription?.status === "trial" && typeof subscription?.days_left === "number" && subscription.days_left <= 7;

  return (
    <div className="min-h-screen flex bg-[#F4F6F8]">
      {/* Sidebar */}
      <aside className="w-[260px] bg-[#0A192F] text-white flex flex-col fixed inset-y-0 left-0" data-testid="sidebar">
        <div className="px-5 py-6 border-b border-white/10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#005E82] flex items-center justify-center">
            <Ambulance size={20} />
          </div>
          <div>
            <div className="font-display font-semibold text-base leading-none">RideWise NEMT</div>
            <div className="text-[11px] text-slate-400 mt-1 tracking-wider uppercase">{roleLabel}</div>
          </div>
        </div>
        <nav className="flex-1 py-4 px-3 space-y-1">
          {visibleLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <l.icon size={16} strokeWidth={1.75} />
              <span className="flex-1">{l.label}</span>
              {l.to === "/app/security" && securityOpenCount > 0 && (
                <span
                  className="bg-rose-600 text-white text-[10px] leading-none font-semibold rounded-full px-1.5 py-0.5"
                  data-testid="security-nav-badge"
                >
                  {securityOpenCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-slate-400 mb-2 truncate">{user?.email}</div>
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 text-sm text-slate-300 hover:text-white transition"
            data-testid="logout-btn"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main: on desktop (>=1280px) the drawer sits beside the content instead of covering it */}
      <main className={`flex-1 ml-[260px] min-h-screen motion-safe:transition-[padding] motion-safe:duration-150 ${previewOpen ? "xl:pr-[560px]" : ""}`}>
        <header className="h-14 border-b border-slate-200 bg-white flex items-center justify-between px-8">
          <div className="text-sm text-slate-500">
            {user?.name} <span className="text-slate-300 px-2">•</span>
            <span className="uppercase tracking-wider text-xs font-semibold text-slate-500">{roleLabel}</span>
          </div>
          <div className="flex items-center gap-2">
            {(user?.role === "agency_admin" || user?.role === "dispatcher") && (
              <AlertsBell onOpenLive={(rid) => navigate(`/app/rides?live=${rid}`)} />
            )}
            {user?.role !== "client_user" && (
              <button
                onClick={() => navigate("/app/rides?new=1")}
                className="btn-primary text-xs"
                data-testid="header-new-ride-btn"
              >
                + New Ride
              </button>
            )}
          </div>
        </header>
        {(subLocked || subTrialWarning) && (
          <div
            className={`px-8 py-2.5 text-sm flex items-center justify-between border-b ${
              subLocked ? "bg-rose-50 text-rose-800 border-rose-200" : "bg-amber-50 text-amber-800 border-amber-200"
            }`}
            data-testid="subscription-banner"
          >
            <span>
              {subLocked
                ? "Your trial has ended. The workspace is locked until the subscription starts."
                : `Trial: ${subscription.days_left} day${subscription.days_left === 1 ? "" : "s"} left.`}
            </span>
            <Link to="/app/subscription" className="font-semibold underline hover:no-underline">
              See subscription
            </Link>
          </div>
        )}
        <div className="p-8 fade-in">
          <Outlet />
        </div>
      </main>
      <FilePreviewDrawer />
    </div>
  );
}
